import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase, portalTokens } from '../lib/supabase';
import { onGeostack, PORTAL_URL } from '../lib/portal';
import api from '../lib/api';

const AuthContext = createContext({});

/**
 * The session to start with. When the portal launched us with tokens, adopt them — unless the
 * shared storage already holds that same session (the normal case on geostack.com), where
 * setSession would only spend a refresh the portal is also relying on.
 */
async function adoptPortalSession() {
  const current = await supabase.auth.getSession();
  if (!portalTokens || current.data.session?.refresh_token === portalTokens.refreshToken) {
    return current;
  }
  const adopted = await supabase.auth.setSession({
    access_token: portalTokens.accessToken,
    refresh_token: portalTokens.refreshToken,
  });
  if (adopted.error) {
    console.error('Could not adopt the portal session', adopted.error);
    return current;
  }
  return adopted;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signingOut, setSigningOut] = useState(false);

  // User id the profile was last requested for. onAuthStateChange also fires for INITIAL_SESSION
  // and every TOKEN_REFRESHED, which must not each re-request the same user's profile.
  const profileUserRef = useRef(null);

  // `force` re-reads for the same user (after a profile-changing write); otherwise a repeat call
  // for the user whose profile is already loaded (or loading) is a no-op.
  const loadProfile = useCallback(async (activeSession, { force = false } = {}) => {
    if (!activeSession) {
      profileUserRef.current = null;
      setProfile(null);
      return null;
    }
    const uid = activeSession.user?.id || null;
    if (!force && uid && profileUserRef.current === uid) return null;
    profileUserRef.current = uid;
    try {
      const { data } = await api.get('/api/auth/profile', force ? { fresh: true } : undefined);
      setProfile(data);
      return data;
    } catch (err) {
      // Profile load failing shouldn't block the app; surface null.
      console.error('Failed to load profile', err);
      profileUserRef.current = null; // let the next auth event retry
      setProfile(null);
      return null;
    }
  }, []);

  useEffect(() => {
    adoptPortalSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      // Render as soon as the session is known (getSession is local/cached).
      // The profile is supplementary, so load it in the background rather than
      // blocking the whole app on a backend round-trip — a slow or cold-started
      // API must not hold the UI on "Loading...".
      setLoading(false);
      loadProfile(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      loadProfile(session);
    });

    return () => subscription?.unsubscribe();
  }, [loadProfile]);

  const signUp = async (email, password, metadata = {}) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: metadata },
    });
    if (error) throw error;
  };

  const signIn = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    // Set before the session goes, so ProtectedRoute sends people to the portal's front door
    // rather than to its sign-in with ?next= back here.
    setSigningOut(true);
    const { error } = await supabase.auth.signOut();
    if (error) {
      // The server could not end the session (e.g. the portal already did). Still drop it here,
      // so this browser is signed out of both apps either way.
      console.error('Sign-out failed on the server; clearing the local session', error);
      await supabase.auth.signOut({ scope: 'local' });
    }
    setProfile(null);
    // The session is the portal's too, so this signed out of both; go back to its front door.
    if (onGeostack()) {
      window.location.replace(PORTAL_URL);
      return;
    }
    setSigningOut(false);
    if (error) throw error;
  };

  const sendPasswordReset = async (email) => {
    // Per-app redirect: prefer an explicit override so this app's reset link
    // always returns here (independent of the portal's Supabase Site URL),
    // falling back to the current origin for local/dev use.
    const redirectTo =
      process.env.REACT_APP_PASSWORD_RESET_REDIRECT ||
      `${window.location.origin}${process.env.PUBLIC_URL}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) throw error;
  };

  const updatePassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    // Clear the must_change_password flag on the profile (best-effort).
    try {
      await api.post('/api/auth/password-changed');
      await loadProfile(session, { force: true });
    } catch (err) {
      console.error('Failed to clear must_change_password', err);
    }
  };

  const updateProfile = async (updates) => {
    const { data } = await api.patch('/api/auth/profile', updates);
    setProfile(data);
    return data;
  };

  const refreshProfile = useCallback(() => loadProfile(session, { force: true }), [loadProfile, session]);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        loading,
        signingOut,
        signUp,
        signIn,
        signOut,
        sendPasswordReset,
        updatePassword,
        updateProfile,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
